import { AppDefinition, AppIcon } from '../types/apps';
import { BaseEntityType, EntityTypeUtils } from '../types/entities';
import { createFileLogger } from '../utils/logger';
import { APP_DEFINITIONS_BY_ID } from '../apps/appDefinitions.index';
import {
  FOLDER_ICON,
  GENERIC_FILE_ICON,
  getIconForEntityType as getFileTypeIcon,
  getIconForFilename,
} from './fileTypeIcons';

const log = createFileLogger('ICON_SERVICE');

interface IconCacheKey {
  entityType: string;
  entityId?: string;
  entityIcon?: string;
  context?: string;
}

class IconService {
  private appDefinitions: Map<string, AppDefinition> = new Map();
  private iconCache: Map<string, AppIcon> = new Map();

  /**
   * Residual icon map — last-resort fallback for apps not covered by the
   * canonical definitions index (APP_DEFINITIONS_BY_ID).
   *
   * Contains only:
   *   - v2 apps that use a legacy `id` field instead of `appId` in their
   *     definition.ts and so are excluded from the canonical index
   *   - Legacy appId aliases (webdoc, pdf, entityBrowser, …)
   *   - Small utility surfaces without a full definition.ts
   *
   * Do NOT add real system apps here. Add the icon to their definition.ts and
   * the canonical index (gui/src/apps/appDefinitions.index.ts) instead.
   */
  private static readonly APP_PHOSPHOR_ICONS: Record<string, AppIcon> = {
    // v2 apps using legacy `id` shape — not yet in the canonical index
    'canvas-v2':       { type: 'phosphor', phosphorIcon: 'PaintBrush',    phosphorWeight: 'fill',    color: '#7c3aed' },
    'sheets-v2':       { type: 'phosphor', phosphorIcon: 'Table',          phosphorWeight: 'fill',    color: '#10b981' },
    'presentations-v2':{ type: 'phosphor', phosphorIcon: 'Presentation',   phosphorWeight: 'fill',    color: '#f97316' },

    // Legacy appId aliases
    'pdf':             { type: 'phosphor', phosphorIcon: 'FilePdf',        phosphorWeight: 'fill',    color: '#dc2626' },
    'webdoc':          { type: 'phosphor', phosphorIcon: 'Globe',          phosphorWeight: 'duotone', color: '#0284c7' },
    'notes':           { type: 'phosphor', phosphorIcon: 'Note',           phosphorWeight: 'fill',    color: '#f59e0b' },
    'prompt':          { type: 'phosphor', phosphorIcon: 'ChatCircle',     phosphorWeight: 'duotone', color: '#3b82f6' },
    'entityBrowser':   { type: 'image',   imagePath: '/icons/riversystem.svg', metadata: { source: 'app', appId: 'entityBrowser', isAppIcon: true } },

    // Utility surfaces without definition.ts
    'console':         { type: 'phosphor', phosphorIcon: 'TerminalWindow', phosphorWeight: 'duotone', color: '#10b981' },
    'entity-view':     { type: 'phosphor', phosphorIcon: 'GridFour',       phosphorWeight: 'duotone', color: '#6366f1' },
    'system':          { type: 'phosphor', phosphorIcon: 'Folder',         phosphorWeight: 'duotone', color: '#f59e0b' },
    'data-source-viewer':{ type: 'phosphor', phosphorIcon: 'Database',     phosphorWeight: 'duotone', color: '#3b82f6' },
    'disco':           { type: 'phosphor', phosphorIcon: 'ChartBar',       phosphorWeight: 'duotone', color: '#06b6d4' },
  };
  // Removed globalEntityIcons - they were interfering with hierarchical resolution
  // All icon mappings are now in commonEntityIcons for proper hierarchy handling

  /**
   * Create a cache key for entity icon lookups
   */
  private createCacheKey(entity: BaseEntityType, context?: string): string {
    const key: IconCacheKey = {
      entityType: entity.entityType,
      entityId: entity._id,
      entityIcon: (entity as any).icon, // Some entities have direct icon field
      context
    };
    return JSON.stringify(key);
  }

  /**
   * Get cached icon result
   */
  private getCachedIcon(cacheKey: string): AppIcon | null {
    return this.iconCache.get(cacheKey) || null;
  }

  /**
   * Cache an icon result
   */
  private setCachedIcon(cacheKey: string, icon: AppIcon): void {
    // Limit cache size to prevent memory leaks
    if (this.iconCache.size > 1000) {
      // Remove oldest entries (simple LRU-style cleanup)
      const oldKeys = Array.from(this.iconCache.keys()).slice(0, 100);
      oldKeys.forEach(key => this.iconCache.delete(key));
    }
    
    this.iconCache.set(cacheKey, icon);
  }

  /**
   * Clear the icon cache (useful for testing or when app definitions change)
   */
  clearCache(): void {
    this.iconCache.clear();
  }

  /**
   * Register an app definition with its icon mappings.
   * Does not overwrite an existing registration — first write wins.
   * Use registerAppForce() when you explicitly need to overwrite.
   */
  registerApp(definition: AppDefinition): void {
    if (this.appDefinitions.has(definition.appId)) return;
    this.appDefinitions.set(definition.appId, definition);
    this.clearCache();
  }

  /**
   * Register multiple app definitions at once, clearing the cache only once.
   * Existing registrations are not overwritten.
   */
  bulkRegisterApps(definitions: Iterable<AppDefinition>): void {
    let added = 0;
    for (const def of definitions) {
      if (def.appId && !this.appDefinitions.has(def.appId)) {
        this.appDefinitions.set(def.appId, def);
        added++;
      }
    }
    if (added > 0) this.clearCache();
  }

  /**
   * Register an app definition, overwriting any previous registration.
   * Only needed for admin/dynamic-reload flows.
   */
  registerAppForce(definition: AppDefinition): void {
    this.appDefinitions.set(definition.appId, definition);
    this.clearCache();
  }

  /**
   * Check if an app is already registered
   */
  isAppRegistered(appId: string): boolean {
    return this.appDefinitions.has(appId);
  }

  /**
   * Synchronous check: returns true if we have a known icon for this appId.
   *
   * Backed by the eagerly-bundled canonical index (APP_DEFINITIONS_BY_ID), so
   * it is safe to call before initializeIconService() completes. Use this to
   * decide whether to render an <AppSquircle> for an entity-less window.
   */
  hasAppIcon(appId: string): boolean {
    return APP_DEFINITIONS_BY_ID.has(appId) ||
      this.appDefinitions.has(appId) ||
      appId in IconService.APP_PHOSPHOR_ICONS;
  }

  /**
   * Get list of registered app IDs for debugging
   */
  getRegisteredApps(): string[] {
    return Array.from(this.appDefinitions.keys());
  }

  /**
   * Internal method: Resolve icon without caching
   * 
   * RESOLUTION ORDER:
   * Priority 0: Self-referencing entity icons (entity.icon = entity._id)
   *             → Allows images to use their thumbnail as icon
   *             → MUST come first to override media type defaults
   * Priority 1: Hardcoded media file checks (Image, Video, Audio, Archive)
   *             → Ensures consistency across all components
   *             → Bypasses app registration and cache issues
   * Priority 2: Direct entity.icon field (database-level custom icon)
   * Priority 3: App-specific entityIcons mapping (from app definitions)
   * Priority 4: App's main icon (fallback for entities owned by app)
   * Priority 5: commonEntityIcons (global fallback mappings)
   * Priority 6: File extension-based lookup (legacy support)
   * Priority 7: Ultimate fallback (generic File icon)
   */
  private async resolveEntityIcon(entity: BaseEntityType, context?: string): Promise<AppIcon> {

    // ========================================
    // PRIORITY 0: Self-Referencing Entity Icons (HIGHEST PRIORITY)
    // ========================================
    // If entity.icon is an entity ID (24-char hex), treat it as an image reference
    // This MUST come first, before media type checks, to allow images to use their own thumbnail as icon
    if (entity.icon && typeof entity.icon === 'string' && /^[0-9a-f]{24}$/i.test(entity.icon)) {
      // Import FILES_ENDPOINTS at runtime to avoid circular dependency
      const { FILES_ENDPOINTS } = await import('../endpoints');
      return {
        type: 'image' as const,
        src: FILES_ENDPOINTS.read(entity.icon, undefined, 'thumbnail'),
        metadata: {
          source: 'entity',
          entityType: entity.entityType,
          isAppIcon: false
        }
      };
    }

    // ========================================
    // PRIORITY 1: File.* type lookup (centralized)
    // ========================================
    // All File.* entity types resolve through the shared fileTypeIcons map
    // so every surface (sidebar, chat chip, tab, viewer) shows the same icon.
    // Falls back in priority order:
    //   1. exact entity type match
    //   2. any ancestor in the File.* hierarchy
    //   3. lookup by filename extension (for weakly-typed File entities)
    if (entity.entityType?.startsWith('File') || entity.entityType === 'File') {
      const fromType = getFileTypeIcon(entity.entityType);
      if (fromType) {
        return {
          ...fromType,
          metadata: { source: 'entity', entityType: entity.entityType, isAppIcon: false },
        };
      }
      const filename = (entity as any).name;
      if (filename) {
        const fromName = getIconForFilename(filename);
        if (fromName) {
          return {
            ...fromName,
            metadata: { source: 'file-extension', entityType: entity.entityType, isAppIcon: false },
          };
        }
      }
    }

    // ========================================
    // PRIORITY 2A: AppInstance Special Handling
    // ========================================
    // AppInstance entities should use their app's icon from the registry or static map
    if (entity.entityType === 'AppInstance' || entity.entityType === 'System.AppInstance') {
      const appId = (entity as any).skeleton?.appId;
      if (appId) {
        const appIcon = this.getAppIcon(appId);
        return {
          ...appIcon,
          metadata: {
            source: 'app',
            appId,
            entityType: entity.entityType,
            isAppIcon: false
          }
        };
      }
      return {
        type: 'phosphor' as const,
        phosphorIcon: 'AppWindow',
        phosphorWeight: 'duotone' as const,
        metadata: { source: 'default' as const, entityType: entity.entityType, isAppIcon: false }
      };
    }

    // ========================================
    // PRIORITY 2B: Integration Entity Special Handling
    // ========================================
    // Integration entities should show their colored brand icons based on entityType or integrationType
    if (entity.entityType === 'Integration' || entity.entityType?.startsWith('Integration.')) {
      // For hierarchical types (Integration.GoogleDrive), extract the service from the type
      let integrationType: string | undefined;
      if (entity.entityType.startsWith('Integration.')) {
        integrationType = entity.entityType.split('.')[1]; // Extract "GoogleDrive" from "Integration.GoogleDrive"
      } else {
        // Legacy: get from skeleton
        const skeleton = (entity as any).skeleton || {};
        integrationType = skeleton.integrationType;
      }
      
      // Map integration types to their icons with brand colors
      const integrationIconMap: Record<string, { icon: string; color?: string }> = {
        'GoogleDrive': { icon: 'simple-icons:googledrive', color: '#4285F4' }, // Google Drive blue
        'Gmail': { icon: '📧' },
        'Slack': { icon: 'simple-icons:slack', color: '#4A154B' }, // Slack aubergine
        'GitHub': { icon: 'simple-icons:github', color: '#181717' }, // GitHub black
        'Notion': { icon: 'simple-icons:notion', color: '#000000' }, // Notion black
        'AppleNote': { icon: '📝' },
        'GoogleCalendar': { icon: 'simple-icons:googlecalendar', color: '#4285F4' },
        // Add more as needed
      };
      
      const iconData = integrationIconMap[integrationType || ''] || { icon: entity.icon || '🔌' };
      const icon = this.parseIcon(iconData.icon);
      
      // Add brand color if specified
      if (iconData.color && icon.type === 'iconify') {
        icon.color = iconData.color;
      }
      
      return {
        ...icon,
        metadata: {
          source: 'entity',
          entityType: entity.entityType,
          isAppIcon: false
        }
      };
    }

    // ========================================
    // PRIORITY 2B: GoogleDriveFile Special Handling
    // ========================================
    // Google Drive files should show their specific file type icon based on entityType or mimeType
    if (entity.entityType === 'GoogleDriveFile' || entity.entityType?.startsWith('External.GoogleDrive')) {
      const skeleton = (entity as any).skeleton || {};
      const mimeType = skeleton.mimeType || '';
      const entityType = entity.entityType;
      
      let iconifyIcon = 'simple-icons:googledrive'; // default
      let color: string | undefined;
      
      // For hierarchical types, we can determine icon from entityType directly
      if (entityType.startsWith('External.GoogleDrive.')) {
        const subtype = entityType.split('.')[2]; // Extract "Document", "Spreadsheet", etc.
        
        switch (subtype) {
          case 'Document':
            iconifyIcon = 'simple-icons:googledocs';
            color = '#4285F4';
            break;
          case 'Spreadsheet':
            iconifyIcon = 'simple-icons:googlesheets';
            color = '#0F9D58';
            break;
          case 'Presentation':
            iconifyIcon = 'simple-icons:googleslides';
            color = '#F4B400';
            break;
          case 'Form':
            iconifyIcon = 'simple-icons:googleforms';
            color = '#7B1FA2';
            break;
          case 'Drawing':
            iconifyIcon = 'simple-icons:googledrive';
            color = '#4285F4';
            break;
          case 'PDF':
            iconifyIcon = 'simple-icons:adobeacrobatreader';
            color = '#EC1C24';
            break;
          case 'Image':
            iconifyIcon = 'simple-icons:googlephotos';
            color = '#4285F4';
            break;
          case 'Video':
            iconifyIcon = 'simple-icons:youtube';
            color = '#FF0000';
            break;
          case 'Audio':
            iconifyIcon = 'simple-icons:googlepodcasts';
            color = '#4285F4';
            break;
        }
      } else {
        // Legacy: determine from mimeType
        // Google Workspace native apps with brand colors
        if (mimeType === 'application/vnd.google-apps.document') {
          iconifyIcon = 'simple-icons:googledocs';
          color = '#4285F4'; // Google Docs blue
        } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
          iconifyIcon = 'simple-icons:googlesheets';
          color = '#0F9D58'; // Google Sheets green
        } else if (mimeType === 'application/vnd.google-apps.presentation') {
          iconifyIcon = 'simple-icons:googleslides';
          color = '#F4B400'; // Google Slides yellow
        } else if (mimeType === 'application/vnd.google-apps.form') {
          iconifyIcon = 'simple-icons:googleforms';
          color = '#7B1FA2'; // Google Forms purple
        } else if (mimeType === 'application/vnd.google-apps.drawing') {
          iconifyIcon = 'simple-icons:googledrive';
          color = '#4285F4';
        } else if (mimeType === 'application/vnd.google-apps.folder') {
          iconifyIcon = 'simple-icons:googledrive';
          color = '#4285F4';
        } else if (mimeType === 'application/vnd.google-apps.site') {
          iconifyIcon = 'simple-icons:googledrive';
          color = '#4285F4';
        }
        // Standard file types
        else if (mimeType === 'application/pdf') {
          iconifyIcon = 'simple-icons:adobeacrobatreader';
          color = '#EC1C24'; // Adobe red
        } else if (mimeType.startsWith('application/vnd.openxmlformats-officedocument.wordprocessing')) {
          iconifyIcon = 'simple-icons:microsoftword';
          color = '#2B579A'; // Word blue
        } else if (mimeType.startsWith('application/vnd.openxmlformats-officedocument.spreadsheet')) {
          iconifyIcon = 'simple-icons:microsoftexcel';
          color = '#217346'; // Excel green
        } else if (mimeType.startsWith('application/vnd.openxmlformats-officedocument.presentation')) {
          iconifyIcon = 'simple-icons:microsoftpowerpoint';
          color = '#B7472A'; // PowerPoint orange-red
        } else if (mimeType === 'application/msword') {
          iconifyIcon = 'simple-icons:microsoftword';
          color = '#2B579A';
        } else if (mimeType === 'application/vnd.ms-excel') {
          iconifyIcon = 'simple-icons:microsoftexcel';
          color = '#217346';
        } else if (mimeType === 'application/vnd.ms-powerpoint') {
          iconifyIcon = 'simple-icons:microsoftpowerpoint';
          color = '#B7472A';
        }
        // Media files
        else if (mimeType.startsWith('image/')) {
          iconifyIcon = 'simple-icons:googlephotos';
          color = '#4285F4'; // Google Photos blue
        } else if (mimeType.startsWith('video/')) {
          iconifyIcon = 'simple-icons:youtube';
          color = '#FF0000'; // YouTube red
        } else if (mimeType.startsWith('audio/')) {
          iconifyIcon = 'simple-icons:googlepodcasts';
          color = '#4285F4';
        }
        // Text and code files
        else if (mimeType === 'text/plain') {
          iconifyIcon = 'simple-icons:googledocs';
          color = '#4285F4';
        } else if (mimeType.startsWith('text/')) {
          iconifyIcon = 'simple-icons:googledrive';
          color = '#4285F4';
        }
      }
      
      return {
        type: 'iconify',
        iconifyIcon,
        color,
        metadata: {
          source: 'entity',
          entityType: entity.entityType,
          isAppIcon: false
        }
      };
    }
    
    // ========================================
    // PRIORITY 2C: LocalFile Special Handling
    // ========================================
    // Local files use the same shared fileTypeIcons map as uploaded File.*
    // entities so a .pdf on disk and a .pdf in storage render identically.
    if (entity.entityType === 'LocalFile' || entity.entityType?.startsWith('External.LocalFile')) {
      const skeleton = (entity as any).skeleton || {};
      const isDirectory = skeleton.isDirectory || false;

      if (isDirectory) {
        return {
          ...FOLDER_ICON,
          metadata: { source: 'entity', entityType: entity.entityType, isAppIcon: false },
        };
      }

      const filename = skeleton.name || (entity as any).name || '';
      const rawExt = (skeleton.extension || '').toLowerCase().replace(/^\./, '');
      const fromExt = rawExt ? getIconForFilename(rawExt) : null;
      const fromName = fromExt || getIconForFilename(filename);

      return {
        ...(fromName || GENERIC_FILE_ICON),
        metadata: { source: 'entity', entityType: entity.entityType, isAppIcon: false },
      };
    }
    
    // ========================================
    // PRIORITY 2C: EntityTrigger Entity Special Handling
    // ========================================
    if (entity.entityType === 'EntityTrigger') {
      return {
        type: 'phosphor',
        phosphorIcon: 'Lightning',
        color: '#f59e0b'
      };
    }

    // PRIORITY 2D: Notification Entity Special Handling
    // ========================================
    // Show different icons based on read status
    if (entity.entityType === 'Notification') {
      const skeleton = (entity as any).skeleton || {};
      const read = skeleton.read;
      return {
        type: 'phosphor',
        phosphorIcon: read ? 'BellSimple' : 'Bell',
        color: read ? '#6b7280' : '#3b82f6'
      };
    }

    // PRIORITY 2E: Order Entity Special Handling
    // ========================================
    // Order entities show different icons based on status
    if (entity.entityType === 'Order') {
      const skeleton = (entity as any).skeleton || {};
      const status = skeleton.status;
      
      let iconifyIcon = 'mdi:receipt';
      let color = '#64748b';  // gray
      
      if (status === 'shipped') {
        iconifyIcon = 'mdi:truck-delivery';
        color = '#10b981';  // green
      } else if (status === 'delivered') {
        iconifyIcon = 'mdi:check-circle';
        color = '#10b981';  // green
      } else if (status === 'cancelled') {
        iconifyIcon = 'mdi:close-circle';
        color = '#ef4444';  // red
      } else if (status === 'fulfillment_error') {
        iconifyIcon = 'mdi:alert-circle';
        color = '#f59e0b';  // amber
      } else if (status === 'pending' || status === 'fulfillment_processing') {
        iconifyIcon = 'mdi:clock-outline';
        color = '#f59e0b';  // amber
      }
      
      return {
        type: 'iconify',
        iconifyIcon,
        color,
        metadata: {
          source: 'entity',
          entityType: entity.entityType,
          isAppIcon: false
        }
      };
    }

    // ========================================
    // PRIORITY 3: Direct Entity Icon Field (Non-Image)
    // ========================================
    // Doc / Document never use per-entity icon overrides. Legacy create paths
    // and onboarding stamped emoji strings (📄, 👋, …) onto entity.icon; those
    // must not eclipse DocsApp.entityIcons (bare riverAppDocSmall.svg). Self-referencing
    // image IDs are already handled in Priority 0.
    if (entity.icon) {
      const isDocType =
        entity.entityType === 'Doc' || entity.entityType === 'Document';
      if (!isDocType) {
        const icon = typeof entity.icon === 'string' ? this.parseIcon(entity.icon) : entity.icon;
        return {
          ...icon,
          metadata: {
            source: 'entity',
            entityType: entity.entityType,
            isAppIcon: false
          }
        };
      }
    }

    // ========================================
    // PRIORITY 3: App-Specific Entity Icons
    // ========================================
    const owningApp = this.getAppForEntityType(entity.entityType);
    
    if (owningApp?.entityIcons) {
      // Try to find icon in hierarchy order (most specific to least specific)
      const hierarchy = EntityTypeUtils.getHierarchy(entity.entityType);
      
      
      for (const hierarchyType of hierarchy) {
        if (owningApp.entityIcons[hierarchyType]) {
          const icon = owningApp.entityIcons[hierarchyType];
          
          
          return {
            ...icon,
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              appId: owningApp.appId,
              isAppIcon: false
            }
          };
        }
      }
    }

    // ========================================
    // PRIORITY 4: App Main Icon Fallback
    // ========================================
    if (owningApp && owningApp.icon) {
      
      const appIcon = typeof owningApp.icon === 'string' 
        ? { type: 'emoji' as const, emoji: owningApp.icon }
        : owningApp.icon;
      
      return {
        ...appIcon,
        metadata: {
          source: 'entity',
          entityType: entity.entityType,
          appId: owningApp.appId,
          isAppIcon: false // Important: this should be an entity icon, not an app icon
        }
      };
    }

    // ========================================
    // PRIORITY 5: Common Entity Icons (non-file)
    // ========================================
    // Note: File.* types are resolved in Priority 1 via fileTypeIcons.ts.
    // Only non-file entity types live here.
    const hierarchy = EntityTypeUtils.getHierarchy(entity.entityType);
    const commonEntityIcons: Record<string, AppIcon> = {
      // Legacy non-hierarchical types (kept for backward compat with old entities)
      'File': GENERIC_FILE_ICON,
      'Document': { type: 'svg', svgPath: '/icons/riverAppDocSmall.svg' },
      'Doc': { type: 'svg', svgPath: '/icons/riverAppDocSmall.svg' },
      'PDF': { type: 'phosphor', phosphorIcon: 'FilePdf', phosphorWeight: 'fill', color: '#dc2626' },
      'Note': { type: 'phosphor', phosphorIcon: 'Note', phosphorWeight: 'duotone', color: '#ea8c0d' },
      'Canvas': { type: 'phosphor', phosphorIcon: 'PaintBrush', phosphorWeight: 'duotone', color: '#7c3aed' },
      'Diagram': { type: 'phosphor', phosphorIcon: 'AppWindow', phosphorWeight: 'duotone', color: '#6366f1' },
      'Image': { type: 'phosphor', phosphorIcon: 'Image', phosphorWeight: 'fill', color: '#0ea5e9' },
      'Folder': { type: 'phosphor', phosphorIcon: 'Folder', phosphorWeight: 'duotone' },
      'Group': { type: 'phosphor', phosphorIcon: 'Folder', phosphorWeight: 'duotone' },
      'Space': { type: 'phosphor', phosphorIcon: 'Cards', phosphorWeight: 'duotone' },
      'WebDocument': { type: 'phosphor', phosphorIcon: 'Globe', phosphorWeight: 'duotone' },
      'Thread': { type: 'phosphor', phosphorIcon: 'ChatCircle', phosphorWeight: 'duotone' },
      'Mail': { type: 'phosphor', phosphorIcon: 'Envelope', phosphorWeight: 'duotone', color: '#3b82f6' },
      'Mail.Thread': { type: 'phosphor', phosphorIcon: 'Envelope', phosphorWeight: 'duotone', color: '#3b82f6' },
      'Message': { type: 'phosphor', phosphorIcon: 'ChatText', phosphorWeight: 'duotone' },
      'Window': { type: 'phosphor', phosphorIcon: 'GridFour', phosphorWeight: 'duotone' },
      'NewWindow': { type: 'phosphor', phosphorIcon: 'GridFour', phosphorWeight: 'duotone' },
      'SearchResults': { type: 'phosphor', phosphorIcon: 'MagnifyingGlass', phosphorWeight: 'duotone' },

      'Content.Note': { type: 'phosphor', phosphorIcon: 'Note', phosphorWeight: 'duotone' },
      'Content.Article': { type: 'phosphor', phosphorIcon: 'Article', phosphorWeight: 'duotone' },
      'System.Group': { type: 'phosphor', phosphorIcon: 'Folder', phosphorWeight: 'duotone' },
      'System.Space': { type: 'phosphor', phosphorIcon: 'Planet', phosphorWeight: 'duotone' },

      'AppProject': { type: 'phosphor', phosphorIcon: 'Code', phosphorWeight: 'duotone' },
      'app_project': { type: 'phosphor', phosphorIcon: 'Code', phosphorWeight: 'duotone' },
      'WebsiteProject': { type: 'phosphor', phosphorIcon: 'Globe', phosphorWeight: 'duotone' },
      'website_project': { type: 'phosphor', phosphorIcon: 'Globe', phosphorWeight: 'duotone' },

      'DataSource': { type: 'phosphor', phosphorIcon: 'Database', phosphorWeight: 'fill' },
      'MarketplaceDataSource': { type: 'phosphor', phosphorIcon: 'Database', phosphorWeight: 'fill' },
      'VerifiedSource': { type: 'phosphor', phosphorIcon: 'Scales', phosphorWeight: 'fill' },
      'MarketplaceVerifiedSource': { type: 'phosphor', phosphorIcon: 'Scales', phosphorWeight: 'fill' },
      'LegalCode': { type: 'phosphor', phosphorIcon: 'Scales', phosphorWeight: 'fill' },

      'ImagePromptRecipe': { type: 'phosphor', phosphorIcon: 'Image', phosphorWeight: 'duotone', color: '#8b5cf6' },
    };
    
    // Check hierarchy for common icon fallbacks
    for (const hierarchyType of hierarchy) {
      const commonIcon = commonEntityIcons[hierarchyType];
      if (commonIcon) {
        return {
          ...commonIcon,
          metadata: {
            source: 'entity',
            entityType: entity.entityType,
            isAppIcon: false
          }
        };
      }
    }

    // ========================================
    // PRIORITY 7: Ultimate Fallback
    // ========================================
    return { 
      type: 'phosphor', 
      phosphorIcon: 'File',
      phosphorWeight: 'duotone',
      metadata: {
        source: 'default',
        entityType: entity.entityType,
        isAppIcon: false
      }
    };
  }

  /**
   * Main method: Get icon for any entity with smart resolution hierarchy
   */
  async getEntityIcon(entity: BaseEntityType, context?: string): Promise<AppIcon> {
    try {
      // Check cache first
      const cacheKey = this.createCacheKey(entity, context);
      const cachedIcon = this.getCachedIcon(cacheKey);
      if (cachedIcon) {
        return cachedIcon;
      }

      // Resolve icon using internal method
      const resolvedIcon = await this.resolveEntityIcon(entity, context);
      
      // Cache the result before returning
      this.setCachedIcon(cacheKey, resolvedIcon);
      
      return resolvedIcon;

    } catch (error) {

      const fallbackIcon: AppIcon = { 
        type: 'phosphor' as const, 
        phosphorIcon: 'Question',
        phosphorWeight: 'duotone',
        metadata: {
          source: 'default' as const,
          entityType: entity.entityType,
          isAppIcon: false
        }
      };
      
      // Even cache the fallback to avoid repeated errors
      const cacheKey = this.createCacheKey(entity, context);
      this.setCachedIcon(cacheKey, fallbackIcon);
      
      return fallbackIcon;
    }
  }

  /**
   * Get icon for a file based on its filename/extension.
   * Delegates to the shared fileTypeIcons map, with registered app icons
   * taking precedence (so an app can claim a file type if needed).
   */
  getFileIcon(filename: string): AppIcon | null {
    const extension = this.getExtension(filename);

    if (extension) {
      for (const [, definition] of this.appDefinitions) {
        if (definition.fileTypeIcons?.[extension]) {
          return definition.fileTypeIcons[extension];
        }
      }
    }

    return getIconForFilename(filename);
  }

  /**
   * Get the Phosphor icon for an app type — used for tab icons.
   * Only checks the static Phosphor map; bypasses registered app definitions
   * that may have image or emoji icons.
   * Returns null if no hardcoded Phosphor icon is defined for this appId.
   */
  getPhosphorAppIcon(appId: string): AppIcon | null {
    const icon = IconService.APP_PHOSPHOR_ICONS[appId];
    if (!icon) return null;
    return {
      ...icon,
      metadata: {
        source: 'app',
        appId,
        isAppIcon: false
      }
    };
  }

  /**
   * Get app icon
   *
   * `opts.flat` requests a container-less variant for UI that renders its own
   * chip/box around the icon (e.g. the chat OpenWindowsIndicator's primary chip).
   * Some app icons (e.g. docsApp.svg) are full macOS-Dock-style "chiplet" assets
   * with a background baked into the image's own pixels — no CSS can strip that,
   * unlike the squircle's own colored background/clip-path/shadow. When the app
   * declares `entityIcons` (a bare, transparent glyph already used elsewhere for
   * inline/tool-renderer icons — see e.g. DocsAppDefinition.entityIcons.Doc), the
   * first one is used as the flat variant instead of the baked chiplet asset.
   */
  getAppIcon(appId: string, opts?: { flat?: boolean }): AppIcon {
    // Runtime-registered definitions win (dynamic/backend apps, plus local defs
    // once initializeIconService() has run). Fall back to the eagerly-bundled
    // canonical index so local app icons resolve SYNCHRONOUSLY even before init
    // completes. Without this, the dock paints "?" placeholders on the first
    // render after a client-side navigation (e.g. landing on a space straight
    // from onboarding, before initializeIconService() populates appDefinitions);
    // a hard refresh masked it because init finished before the dock mounted.
    // Mirrors hasAppIcon(), which already consults APP_DEFINITIONS_BY_ID.
    const app = this.appDefinitions.get(appId) ?? APP_DEFINITIONS_BY_ID.get(appId);

    if (opts?.flat && app?.entityIcons) {
      const flatEntityType = app.entityTypes?.find((t) => app.entityIcons?.[t]);
      const flatIcon = flatEntityType ? app.entityIcons?.[flatEntityType] : undefined;
      if (flatIcon) {
        return {
          ...flatIcon,
          metadata: { source: 'app', appId, isAppIcon: false },
        };
      }
    }

    if (app?.icon) {
      const icon = typeof app.icon === 'string' 
        ? { type: 'emoji' as const, emoji: app.icon }
        : { ...app.icon };

      // Color resolution for the squircle tint.
      // Icon objects from definition.ts carry color directly (icon.color).
      // As a fallback, some legacy definitions declare color as a sibling
      // field (app.color) rather than inside the icon object.
      if (!icon.color) {
        const definitionColor = (app as any).color;
        if (definitionColor) icon.color = definitionColor;
      }
      
      // Add metadata to indicate this is an app icon
      return {
        ...icon,
        metadata: {
          source: 'app',
          appId: appId,
          isAppIcon: true
        }
      };
    }
    
    // Fallback to hardcoded Phosphor icons
    const fallbackIcon = IconService.APP_PHOSPHOR_ICONS[appId];
    if (fallbackIcon) {
      return {
        ...fallbackIcon,
        metadata: {
          source: 'app',
          appId: appId,
          isAppIcon: true
        }
      };
    }
    
    return { 
      type: 'phosphor', 
      phosphorIcon: 'Question',
      phosphorWeight: 'duotone',
      metadata: {
        source: 'default',
        appId: appId,
        isAppIcon: true
      }
    };
  }

  /**
   * Find which app owns/manages a specific entity type using hierarchical matching
   */
  private getAppForEntityType(entityType: string): AppDefinition | null {
    // Get the hierarchy for this entity type (including self)
    const hierarchy = EntityTypeUtils.getHierarchy(entityType);

    // Runtime-registered definitions win (dynamic/backend apps, plus local defs
    // once initializeIconService() has run). Fall back to the eagerly-bundled
    // canonical index so an app-owned entity type resolves SYNCHRONOUSLY even
    // before init completes. Without this, every entity icon that mounts during
    // a fresh space load (sidebar rows, window tabs, the create menu) resolves
    // to the generic File fallback and keeps it for its lifetime, since Icon's
    // resolution effect never reruns. Mirrors getAppIcon()/hasAppIcon(), which
    // already consult APP_DEFINITIONS_BY_ID.
    return (
      this.findAppOwningEntityType(this.appDefinitions.values(), hierarchy) ??
      this.findAppOwningEntityType(APP_DEFINITIONS_BY_ID.values(), hierarchy)
    );
  }

  /**
   * First definition in `definitions` that claims this entity type or any of
   * its ancestors.
   */
  private findAppOwningEntityType(
    definitions: Iterable<AppDefinition>,
    hierarchy: string[]
  ): AppDefinition | null {
    for (const definition of definitions) {
      const entityTypes = definition.entityTypes;
      if (!Array.isArray(entityTypes)) {
        continue;
      }

      if (hierarchy.some((hierarchyType) => entityTypes.includes(hierarchyType))) {
        return definition;
      }
    }

    return null;
  }

  /**
   * Parse a string icon into AppIcon format
   */
  private parseIcon(icon: string): AppIcon {
    // Check if this is an iconify icon (format: "provider:icon-name")
    if (icon.includes(':') && (
      icon.startsWith('simple-icons:') ||
      icon.startsWith('lucide:') ||
      icon.startsWith('mdi:') ||
      icon.startsWith('heroicons:') ||
      icon.startsWith('fa:') ||
      icon.startsWith('carbon:')
    )) {
      return {
        type: 'iconify',
        iconifyIcon: icon,
      };
    }
    
    // Check if this is a Phosphor icon (PascalCase name without special characters)
    // Phosphor icons are named like: PencilSimple, ListBullets, ArrowRight, etc.
    if (/^[A-Z][a-zA-Z0-9]*$/.test(icon)) {
      return {
        type: 'phosphor',
        phosphorIcon: icon,
        phosphorWeight: 'duotone'
      };
    }
    
    // Otherwise treat as emoji
    return { type: 'emoji', emoji: icon };
  }

  /**
   * Extract file extension from filename
   */
  private getExtension(filename: string): string {
    const parts = filename.split('.');
    if (parts.length < 2) return '';
    return '.' + parts[parts.length - 1].toLowerCase();
  }



  /**
   * Update global entity icon fallbacks (deprecated - use commonEntityIcons)
   */
  updateGlobalEntityIcon(entityType: string, icon: AppIcon): void {
    console.warn('[iconService] updateGlobalEntityIcon is deprecated - modify commonEntityIcons instead');
  }

  /**
   * Get global entity icon for a specific entity type (deprecated)
   */
  getGlobalEntityIcon(entityType: string): AppIcon | null {
    console.warn('[iconService] getGlobalEntityIcon is deprecated - using commonEntityIcons');
    return null;
  }

  /**
   * Get icon for section/category names (used in context selectors)
   */
  getSectionIcon(sectionName: string): AppIcon {
    // Remove emoji prefixes for consistent lookup
    const cleanSectionName = sectionName.replace(/^[📑🪟]\s*/, '');
    
    const sectionIcons: Record<string, AppIcon> = {
      'Current Context': { type: 'phosphor', phosphorIcon: 'Cards', phosphorWeight: 'duotone' },
      'Windows': { type: 'phosphor', phosphorIcon: 'WindowsLogo', phosphorWeight: 'duotone' },
      'Files': { type: 'phosphor', phosphorIcon: 'File', phosphorWeight: 'duotone' },
      'WebDocuments': { type: 'phosphor', phosphorIcon: 'Globe', phosphorWeight: 'duotone' },
      'Notes': { type: 'phosphor', phosphorIcon: 'Note', phosphorWeight: 'duotone', color: '#ea8c0d' },
      'Brainstorming': { type: 'phosphor', phosphorIcon: 'Lightbulb', phosphorWeight: 'duotone' },
      'Groups': { type: 'phosphor', phosphorIcon: 'Folder', phosphorWeight: 'duotone' },
      'Docs': { type: 'phosphor', phosphorIcon: 'FileText', phosphorWeight: 'duotone' },
      'Prompts': { type: 'phosphor', phosphorIcon: 'ChatCircle', phosphorWeight: 'duotone' },
      'Workflows': { type: 'phosphor', phosphorIcon: 'Lightning', phosphorWeight: 'duotone' },
      'InstalledApps': { type: 'phosphor', phosphorIcon: 'DeviceTablet', phosphorWeight: 'duotone' },
      'MarketplaceItems': { type: 'phosphor', phosphorIcon: 'ShoppingBag', phosphorWeight: 'duotone' },
      'Categories': { type: 'phosphor', phosphorIcon: 'List', phosphorWeight: 'duotone' },
    };

    return sectionIcons[cleanSectionName] || { type: 'phosphor', phosphorIcon: 'File', phosphorWeight: 'duotone' };
  }
}

// Export singleton instance
export const iconService = new IconService();

// Export class for testing
export { IconService };